import { StandardEvents } from '@shopify/events';

/**
 * Size-first quick add on product cards (see snippets/vd-quick-add.liquid).
 * A size tap writes the variant into the hidden `id` input and submits Horizon's
 * product-form-component, which adds the line, refreshes the cart and opens the drawer.
 * On touch screens the "+" toggle opens the size bar; one bar is open at a time.
 */
class VdQuickAdd extends HTMLElement {
  #controller = new AbortController();
  /** @type {AbortController | undefined} */
  #outsideController;
  /** @type {HTMLButtonElement | null} */
  #pending = null;
  /** @type {number | undefined} */
  #resetTimer;

  connectedCallback() {
    const { signal } = this.#controller;
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#outsideController?.abort();
    clearTimeout(this.#resetTimer);
  }

  get toggle() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('.vd-qa__toggle'));
  }

  open() {
    for (const other of document.querySelectorAll('vd-quick-add[data-open]')) {
      if (other !== this && other instanceof VdQuickAdd) other.close();
    }
    this.setAttribute('data-open', '');
    this.toggle?.setAttribute('aria-expanded', 'true');
    /** @type {HTMLButtonElement | null} */ (this.querySelector('.vd-qa__size:not(:disabled)'))?.focus({
      preventScroll: true,
    });

    this.#outsideController = new AbortController();
    document.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target instanceof Node && !this.contains(event.target)) this.close();
      },
      { capture: true, signal: this.#outsideController.signal }
    );
  }

  close() {
    this.removeAttribute('data-open');
    this.toggle?.setAttribute('aria-expanded', 'false');
    this.#outsideController?.abort();
  }

  /** @param {MouseEvent} event */
  #handleClick = (event) => {
    // Clicks inside the bar never reach product-card's "navigate to product" handler
    event.stopPropagation();
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('.vd-qa__toggle')) {
      event.preventDefault();
      this.open();
      return;
    }

    const size = /** @type {HTMLButtonElement | null} */ (event.target.closest('.vd-qa__size'));
    if (!size || size.disabled || this.#pending) return;

    const form = this.querySelector('form');
    const input = /** @type {HTMLInputElement | null} */ (form?.querySelector('input[name="id"]'));
    if (!form || !input || !size.dataset.variantId) return;

    clearTimeout(this.#resetTimer);
    for (const added of this.querySelectorAll('[data-added]')) added.removeAttribute('data-added');
    delete this.dataset.state;

    input.value = size.dataset.variantId;
    size.setAttribute('data-loading', '');
    this.#pending = size;
    form.requestSubmit();
  };

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
    this.close();
    this.toggle?.focus();
  };

  /** @param {Event & { promise?: Promise<{ detail?: { didError?: boolean } }> }} event */
  #handleCartUpdate = (event) => {
    const size = this.#pending;
    if (!size) return;

    /** @param {boolean} ok */
    const settle = (ok) => {
      size.removeAttribute('data-loading');
      this.#pending = null;
      this.dataset.state = ok ? 'added' : 'error';
      if (ok) size.setAttribute('data-added', '');
      this.#resetTimer = setTimeout(() => {
        size.removeAttribute('data-added');
        delete this.dataset.state;
        if (ok) this.close();
      }, 1800);
    };

    if (event.promise) {
      event.promise.then(({ detail } = {}) => settle(!detail?.didError)).catch(() => settle(false));
    } else {
      settle(true);
    }
  };
}

if (!customElements.get('vd-quick-add')) customElements.define('vd-quick-add', VdQuickAdd);
